import workflowDashboardImage from "@/assets/automations-dashboard.webp";
import { Button } from "@/components/flexkit/Button";
import { Pill } from "@/components/podkit/status/Pill";
import { toast } from "@/components/podkit/toasts/use-toast";
import { Heading1, Heading3 } from "@/components/podkit/typography/Headings";
import { Text as Paragraph } from "@/components/podkit/typography/Text";
import { usePreferenceValue } from "@/hooks/use-preference-value";
import { useSegmentTrack } from "@/hooks/use-segment";
import { useGetAccount } from "@/queries/account-queries";
import { AlertTriangleIcon, GitBranchIcon, ShieldCheckIcon } from "lucide-react";
import { type FC, type ReactNode, useCallback } from "react";

const WORKFLOWS_EARLY_ACCESS_REQUESTED = "WORKFLOWS_EARLY_ACCESS_REQUESTED";

export const AutomationsEducationPage: FC = () => {
    const segmentTrack = useSegmentTrack();
    const { data: account } = useGetAccount();

    const { value: earlyAccessRequestedValue, setValueAsync: setEarlyAccessRequestedValue } = usePreferenceValue(
        WORKFLOWS_EARLY_ACCESS_REQUESTED,
    );
    const earlyAccessRequested = earlyAccessRequestedValue === "true";

    const handleRequestEarlyAccess = useCallback(async () => {
        try {
            segmentTrack("waitlist_requested", { type: "workflows", email: account?.email });
            await setEarlyAccessRequestedValue("true");
            toast({
                title: "Early access requested",
                description: "We'll be in touch about early access to workflows.",
            });
        } catch (error) {
            toast({
                title: "Failed to request early access",
                description: error instanceof Error ? error.message : "An unknown error occurred.",
            });
        }
    }, [account?.email, segmentTrack, setEarlyAccessRequestedValue]);

    return (
        <div className="flex flex-col items-center justify-center py-4">
            <div className="w-full max-w-7xl">
                <Pill variant="neutral" size="lg">
                    Available in preview
                </Pill>
                <div className="my-16 flex max-w-full flex-row flex-wrap justify-between gap-8">
                    <div className="max-w-xl">
                        <Heading1 className="mb-4 whitespace-normal">Scale change across repos.</Heading1>
                        <Paragraph className="text-secondary mb-8 text-balance text-lg">
                            Chain agents and commands into multi-step automations that run across your entire codebase -
                            scheduled or event driven.
                        </Paragraph>
                        <div className="mt-12 flex flex-col items-center justify-start gap-4 sm:flex-row">
                            <Button
                                size="lg"
                                onClick={handleRequestEarlyAccess}
                                disabled={earlyAccessRequested}
                                data-tracking-id="request-early-access-workflows-education-page"
                            >
                                {earlyAccessRequested ? "Early Access Requested" : "Request early access"}
                            </Button>
                        </div>
                    </div>

                    <img
                        src={workflowDashboardImage}
                        alt="Ona Workflows dashboard"
                        className="mb-8 w-[42rem] max-w-full rounded-lg shadow-xl"
                        width={2732}
                        height={1536}
                    />
                </div>

                <div className="flex flex-col gap-8 md:flex-row">
                    <WorkflowsEducationCard
                        icon={<ShieldCheckIcon size={24} />}
                        title="Enforce Compliance and Governance"
                        description="Automate PR reviews and compliance checks with audit-ready reports and documentation."
                    />

                    <WorkflowsEducationCard
                        icon={<AlertTriangleIcon size={24} />}
                        title="Flag and fix vulnerabilities"
                        description="Automate scans, patching, and open pull requests for review - reducing remediation time to hours."
                    />

                    <WorkflowsEducationCard
                        icon={<GitBranchIcon size={24} />}
                        title="Standardize and Migrate at Scale"
                        description="Upgrade language versions, unify configurations, or migrate infrastructure through a single defined automation."
                    />
                </div>

                <div className="mt-12 text-center">
                    <Paragraph className="text-lg text-content-secondary">
                        … or design personal workflows that let agents handle the routine, so you can stay focused on
                        building.
                    </Paragraph>
                </div>
            </div>
        </div>
    );
};

const WorkflowsEducationCard: FC<{
    icon?: ReactNode;
    title: string;
    description: string;
}> = ({ icon, title, description }) => {
    return (
        <div className="flex flex-col items-center md:items-start">
            {icon}
            <div className="mt-2 flex flex-row items-center gap-2 text-left">
                <Heading3 className="mb-2">{title}</Heading3>
            </div>
            <Paragraph className="text-left text-content-secondary">{description}</Paragraph>
        </div>
    );
};
